import { inject, Injectable, Type } from '@angular/core';
import { ChangePinDialogComponent } from '@merchant-portal/app/core/pin/change-pin/change-pin-dialog/change-pin-dialog.component';
import { CheckPinDialogComponent } from '@merchant-portal/app/core/pin/check-pin/check-pin-dialog/check-pin-dialog.component';
import { PinDialogComponent } from '@merchant-portal/app/core/pin/dialog/pin-dialog.component';
import { ForgotPinDialogComponent } from '@merchant-portal/app/core/pin/forget-pin/forgot-pin-dialog/forgot-pin-dialog.component';
import { DisbursementHistoryDetailComponent } from '@merchant-portal/app/features/dashboard/pages/disbursement/disbursement-history/all-disbursement-history/disbursement-history-detail/disbursement-history-detail.component';
import { WithdrawalHistoryDetailComponent } from '@merchant-portal/app/features/dashboard/pages/payment/withdrawal-history/withdrawal-history-detail/withdrawal-history-detail.component';
import { WithdrawalRequestDialogComponent } from '@merchant-portal/app/features/dashboard/pages/payment/withrawal-request-dialog/withdrawal-request.component-dialog';
import { CreateEditTeamMemberComponent } from '@merchant-portal/app/features/dashboard/pages/setting/team-member/create-edit-team-member/create-edit-team-member.component';
import { BulkTransactionItemConfirmation } from '@merchant-portal/app/models/disbursement/bulk-transaction/bulk-transaction.interface';
import { SingleTransactionData } from '@merchant-portal/app/models/disbursement/single-transaction/single-transaction.interface';
import {
  IWithdrawalPrepareResponse,
  WithdrawalConfirmation,
} from '@merchant-portal/app/models/payment/payment.interface';
import { environment } from '@merchant-portal/environments/environment';
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { AuthenticatorSetupDialogComponent } from './authenticator-setup-dialog/authenticator-setup-dialog.component';
import { BeneficiaryLimitExceededDialogComponent } from './beneficiary-limit-exceeded-dialog/beneficiary-limit-exceeded-dialog.component';
import { BoxedOptionSelectionDialogComponent } from './boxed-option-selection-dialog/boxed-option-selection-dialog.component';
import { BulkTransactionConfirmationDialogComponent } from './bulk-transaction-confirmation-dialog/bulk-transaction-confirmation-dialog.component';
import { BulkTransactionSummaryDialogComponent } from './bulk-transaction-summary-dialog/bulk-transaction-summary-dialog.component';
import { ChangePasswordDialogComponent } from './change-password-dialog/change-password-dialog.component';
import { CheckPasswordDialogComponent } from './check-password-dialog/check-password-dialog.component';
import { ComingSoonDialogComponent } from './coming-soon-dialog/coming-soon-dialog.component';
import { ConfirmationDialogComponent } from './confirmation-dialog/confirmation-dialog.component';
import {
  DashboardTutorialDialogComponent,
  IDashboardTutorialDialog,
} from './dashboard-tutorial-dialog/dashboard-tutorial-dialog.component';
import { EnableAuthenticatorDialogComponent } from './enable-authenticator-dialog/enable-authenticator-dialog.component';
import { ErrorDialogComponent } from './error-dialog/error-dialog.component';
import { HelpCenterDialogComponent } from './help-center-dialog/help-center-dialog.component';
import { InvitationDialogComponent } from './invitation-dialog/invitation-dialog.component';
import { PaymentLinkCreatedDialogComponent } from './payment-link-created-dialog/payment-link-created-dialog.component';
import { SessionEndedDialogComponent } from './session-ended-dialog/session-ended-dialog.component';
import { ShowRolePermissionDialogComponent } from './show-role-permission-dialog/show-role-permission-dialog.component';
import {
  ISinggleFormDialogData,
  SinggleFormDialogComponent,
} from './singgle-form-dialog/singgle-form-dialog.component';
import { SuccessDialogComponent } from './success-dialog/success-dialog.component';
import { TopUpDialogComponent } from './top-up-dialog/top-up-dialog.component';
import { TransactionConfirmationDialogComponent } from './transaction-confirmation-dialog/transaction-confirmation-dialog.component';
import { TransactionCreatedDialogComponent } from './transaction-created-dialog/transaction-created-dialog.component';
import { TransactionRejectDialogComponent } from './transaction-reject-dialog/transaction-reject-dialog/transaction-reject-dialog.component';
import { UploadDocumentDialogComponent } from './upload-document-dialog/upload-document-dialog.component';
import { UploadCompleteDialogComponent } from './upload-transaction-complete-dialog/upload-transaction-complete-dialog.component';
import { VerifyChangePasswordDialogComponent } from './verify-change-password-dialog/verify-change-password-dialog.component';
import { WaitingCardSetupDialogComponent } from './waiting-card-setup-dialog/waiting-card-setup-dialog.component';
import { WithdrawalConfirmationDialogComponent } from './withdrawal-confirmation-dialog/withdrawal-confirmation-dialog.component';

export interface DialogBody {
  width: string;
  height: string;
  body: {
    title: string;
    message: string;
    img?: string; // illustration img
    closeText: string | undefined;
    confirmText: string | undefined;
    isDoNothing?: boolean;
    illustration?: string;
  };
  closable: boolean;
  styleClass?: string;
}

interface IBaseDailog<T> {
  width: string;
  height: string;
  body: T;
  closable?: boolean;
}

interface IRolePermisisonDialog {
  roleName: string;
  isDefaultRole: boolean;
  uuid: string;
}

export interface IInvitationDialogBody {
  title: string;
  message: string;
  confirmText?: string;
  closeText?: string;
  formData?: {
    memberName?: string;
    email?: string;
    role?: string;
  };
}

export interface IBoxedOptionSelectionDialogBody {
  title: string;
  subTitle: string;
  value: any;
}

export interface IBeneficiaryLimitDialogBody {
  title: string;
  subTitle: string;
  amount: number;
  total: number;
}

@Injectable({
  providedIn: 'root',
})
export class DialogComponentService {
  ref: DynamicDialogRef | undefined;
  private dialogService: DialogService = inject(DialogService);
  private readonly defaultDialogConfig: DynamicDialogConfig = {
    modal: true,
    dismissableMask: false,
    focusOnShow: true,
  };

  private open<T>(component: Type<T>, config: DynamicDialogConfig): DynamicDialogRef {
    return this.dialogService.open(component, {
      ...this.defaultDialogConfig,
      ...config,
    });
  }

  openHelpCenterDialog(obj: any) {
    const { width, height, body } = obj;
    this.ref = this.open(HelpCenterDialogComponent, {
      width: width,
      height: height,
      closable: false,
      data: body,
    });
  }

  openTopUpDialog(obj: any) {
    const { width, height, body } = obj;
    this.ref = this.open(TopUpDialogComponent, {
      width: width,
      height: height,
      closable: true,
      styleClass: 'headerDialog',
      data: body,
    });

    return this.ref;
  }

  openSuccessDialog(
    obj: IBaseDailog<{
      title?: string;
      message?: string;
      closeText?: string;
      closeValue?: boolean;
    }>
  ) {
    const { width, height, body, closable } = obj;
    const dialog = this.open(SuccessDialogComponent, {
      width: width,
      height: height,
      closable: closable ?? false,
      data: body,
    });
    this.ref = dialog;

    return dialog;
  }

  openTransactionConfirmationDialog(obj: {
    title?: string;
    message?: string;
    width?: string;
    height?: string;
    body?: SingleTransactionData;
    bulkContent?: BulkTransactionItemConfirmation;
  }) {
    const { width, height, body, title, message, bulkContent } = obj;
    const data = this.open(TransactionConfirmationDialogComponent, {
      width: width,
      height: height,
      closable: true,
      dismissableMask: false,
      modal: true,
      data: {
        content: body,
        title,
        message,
        bulkContent: bulkContent,
      },
    });
    this.ref = data;
    return data;
  }

  openTransactionRejectDialog(obj: {
    title: string;
    message: string;
    width: string;
    height: string;
    isSpecify?: boolean;
  }) {
    const { width, height, title, message, isSpecify } = obj;
    const dialog = this.open(TransactionRejectDialogComponent, {
      width: width,
      height: height,
      closable: true,
      dismissableMask: false,
      modal: true,
      styleClass: 'rejectDialog',
      data: { title, message, isSpecify },
    });
    this.ref = dialog;
    return dialog;
  }

  openErrorDialog(obj: any) {
    const { width, height, body } = obj;
    const dialog = this.open(ErrorDialogComponent, {
      width: width,
      height: height,
      closable: true,
      data: body,
    });
    this.ref = dialog;
    return dialog;
  }

  openBulkTransactionConfirmationDialog(obj: any) {
    const { width, height, body } = obj;
    const dialog = this.open(BulkTransactionConfirmationDialogComponent, {
      width: width,
      height: height,
      closable: true,
      dismissableMask: false,
      modal: true,
      data: body,
    });
    this.ref = dialog;
    return dialog;
  }

  openTransactionCreatedDialog(obj: any) {
    const { width, height, body } = obj;
    const dialog = this.open(TransactionCreatedDialogComponent, {
      width: width,
      height: height,
      closable: false,
      data: body,
    });

    this.ref = dialog;

    return dialog;
  }

  openCreateEditTeamMemberDialog(obj: any) {
    const { width, height, body } = obj;
    const dialog = this.open(CreateEditTeamMemberComponent, {
      width: width,
      height: height,
      closable: false,
      data: body,
    });
    this.ref = dialog;
    return dialog;
  }

  openConfirmationDialog(obj: DialogBody) {
    const { width, height, body, closable, styleClass } = obj;
    const dialog = this.open(ConfirmationDialogComponent, {
      width: width,
      height: height,
      closable: closable,
      data: body,
      styleClass: styleClass || 'success-dialog',
    });
    this.ref = dialog;
    return this.ref;
  }

  openChangePinDialog(obj: { width: string; height: string }) {
    const { width, height } = obj;
    const dialog = this.open(ChangePinDialogComponent, {
      width: width,
      height: height,
      closable: false,
    });

    this.ref = dialog;
    return this.ref;
  }

  openPinValidationDialog(obj: {
    width: string;
    height: string;
    data?: {
      isWithoutChecking?: boolean;
      error?: string;
    };
  }) {
    const { data } = obj;
    const dialog = this.open(CheckPinDialogComponent, {
      height: 'fit-content',
      width: '450px',
      data: data,
    });
    this.ref = dialog;
    return this.ref;
  }

  openNewPINDialog(opt: {
    width: string;
    height: string;
    data?: {
      mode: string;
      title?: string;
      subtitle?: string;
      shouldSkipDirectCreation?: boolean;
      shouldSkipValidation?: boolean;
      shouldSkipSuccessInfo?: boolean;
    };
  }) {
    const dialog = this.open(PinDialogComponent, {
      height: opt.height ?? 'fit-content',
      width: opt.width ?? '450px',
      closeOnEscape: true,
      closable: false,
      data: opt.data,
    });
    this.ref = dialog;
    return this.ref;
  }
  openBulkTransactionSummaryDialog(obj: any) {
    const { width, height, body } = obj;
    const dialog = this.open(BulkTransactionSummaryDialogComponent, {
      width: width,
      height: height,
      closable: true,
      data: body,
    });
    this.ref = dialog;
    return dialog;
  }

  openChangePasswordDialog(data: { email: string; currentPassword: string }) {
    const dialog = this.open(ChangePasswordDialogComponent, {
      width: '520px',
      height: 'auto',
      closable: true,
      modal: true,
      styleClass: 'change-password-dialog-modal',
      data,
    });
    this.ref = dialog;
    return dialog;
  }

  openCheckPasswordDialog() {
    const dialog = this.open(CheckPasswordDialogComponent, {
      width: '480px',
      height: 'auto',
      closable: true,
      modal: true,
      styleClass: 'password-dialog-modal',
    });
    this.ref = dialog;
    return dialog;
  }

  openVerifyChangePasswordDialog(data: {
    otpToken: string;
    password: string;
    newPassword: string;
    resendDelaySeconds?: number;
    twoFactorAuthMethod?: 'OTP' | 'TOTP' | string;
  }) {
    const dialog = this.open(VerifyChangePasswordDialogComponent, {
      width: '520px',
      height: 'auto',
      closable: true,
      modal: true,
      styleClass: 'verify-change-password-dialog-modal',
      data,
    });
    this.ref = dialog;
    return dialog;
  }

  openShowRolePermissionDialog(obj: IBaseDailog<IRolePermisisonDialog>) {
    const { width, height, body } = obj;
    const isMobile = window.innerWidth <= 768;
    const dialog = this.open(ShowRolePermissionDialogComponent, {
      width: isMobile ? '100vw' : width,
      height: isMobile ? '100vh' : height,
      closable: true,
      data: body,
      contentStyle: isMobile ? { padding: '0', overflow: 'auto' } : undefined,
      modal: true,
    });
    this.ref = dialog;
    return dialog;
  }
  openDisbursementHistoryDetailDialog(id: string, title: string) {
    const dialog = this.open(DisbursementHistoryDetailComponent, {
      width: '500px',
      closable: true,
      header: title,
      data: { id },
      styleClass: 'disbursementHistoryDetailDialog',
    });
    this.ref = dialog;
    return dialog;
  }
  openUploadCompleteDialog(obj: DialogBody) {
    const { width, height, body, closable } = obj;
    const dialog = this.open(UploadCompleteDialogComponent, {
      width: width,
      height: height,
      closable: closable,
      data: body,
      styleClass: 'headerDialog',
    });
    this.ref = dialog;
    return this.ref;
  }

  openSinggleFormDialog(obj: ISinggleFormDialogData) {
    const { title, subtitle, buttonText, formLabel, store } = obj;
    const dialog = this.open(SinggleFormDialogComponent, {
      width: '1000px',
      height: '700px',
      closable: true,
      data: { title, subtitle, buttonText, formLabel, store },
    });
    this.ref = dialog;
    return this.ref;
  }

  openResetPinDialog() {
    const dialog = this.open(ForgotPinDialogComponent, {
      width: '500px',
      height: 'auto',
      closable: false,
    });
    this.ref = dialog;
    return this.ref;
  }

  openInvitationUserDialog(obj: {
    width: string;
    height: string;
    closable: boolean;
    body?: {
      formOrder?: string[];
      fromData?: any;
      closeText?: string;
      confirmText?: string;
      title?: string;
      message?: string;
    };
  }) {
    const { width, height, closable, body } = obj;
    const dialog = this.open(InvitationDialogComponent, {
      width: width,
      height: height,
      closable: closable,
      data: body,
    });
    this.ref = dialog;
    return dialog;
  }

  openWithdrawalHistoryDetailDialog(id: string, title: string) {
    const dialog = this.open(WithdrawalHistoryDetailComponent, {
      width: '500px',
      closable: true,
      header: title,
      data: { id },
      styleClass: 'withdrawalHistoryDetailDialog',
    });
    this.ref = dialog;
    return dialog;
  }

  openWithdrawalRequestDialog(data: IWithdrawalPrepareResponse['data']) {
    const dialog = this.open(WithdrawalRequestDialogComponent, {
      width: '500px',
      closable: true,
      styleClass: 'withdrawalRequestlDialog',
      data: data,
    });
    this.ref = dialog;
    return dialog;
  }

  openWithdrawalConfirmationDialog(obj: {
    title?: string;
    message?: string;
    width?: string;
    height?: string;
    body?: WithdrawalConfirmation;
  }) {
    const { width, height, body, title, message } = obj;
    const data = this.open(WithdrawalConfirmationDialogComponent, {
      width: width,
      height: height,
      closable: true,
      data: {
        content: body,
        title,
        message,
      },
    });
    this.ref = data;
    return data;
  }

  openBoxedOptionSelectionDialog(obj: {
    title?: string;
    subTitle?: string;
    width?: string;
    height?: string;
    body?: IBoxedOptionSelectionDialogBody[];
  }) {
    const { width, height, body, title, subTitle } = obj;
    const data = this.open(BoxedOptionSelectionDialogComponent, {
      width: width,
      height: height,
      closable: true,
      data: {
        title,
        subTitle: subTitle,
        content: body,
      },
    });
    this.ref = data;
    return data;
  }

  openBeneficiaryLimitExceededDialog(payload: {
    width?: string;
    height?: string;
    body?: IBeneficiaryLimitDialogBody;
  }) {
    const data = this.open(BeneficiaryLimitExceededDialogComponent, {
      width: payload.width,
      height: payload.height,
      closable: true,
      dismissableMask: false,
      modal: true,
      data: {
        title: payload.body?.title,
        subTitle: payload.body?.subTitle,
        total: payload.body?.total,
        amount: payload.body?.amount,
      },
    });

    this.ref = data;
    return data;
  }

  /**
   * Opens the coming soon banner dialog if feature flag is enabled and the specific user hasn't seen it yet
   * @param email The user's email address
   * @param force If true, shows the banner regardless of localStorage state (for testing)
   * @returns The dialog reference or null if the dialog was not opened
   */
  openComingSoonBanner(email?: string, force: boolean = false) {
    // Check if feature flag is enabled
    if (!environment.flag.show_coming_soon_banner && !force) {
      return null;
    }

    // If no email is provided, we can't track per-user
    if (!email && !force) {
      return null;
    }

    // Check if this specific user has already seen the banner
    if (!force && email) {
      const seenBannerJson = localStorage.getItem('comingSoonBannerSeen_v2');
      if (seenBannerJson) {
        try {
          const seenBannerUsers = JSON.parse(seenBannerJson);
          if (seenBannerUsers[email]) {
            return null;
          }
        } catch (e) {
          // Continue if localStorage format is invalid
        }
      }
    }

    // Open the dialog
    const dialog = this.open(ComingSoonDialogComponent, {
      width: '500px',
      height: 'auto',
      closable: false,
      showHeader: false,
      modal: true,
      styleClass: 'coming-soon-dialog',
      data: { userEmail: email },
    });

    this.ref = dialog;
    return dialog;
  }

  openDashboardTutorial(payload: {
    width: string;
    height: string;
    data: IDashboardTutorialDialog;
  }) {
    // Open the dialog
    const dialog = this.open(DashboardTutorialDialogComponent, {
      width: payload.width,
      height: payload.height,
      closable: false,
      showHeader: false,
      modal: true,
      data: payload.data,
    });

    this.ref = dialog;
    return dialog;
  }

  openUploadDocumentDialog() {
    const dialog = this.open(UploadDocumentDialogComponent, {
      width: '500px',
      height: 'auto',
      closable: false,
      showHeader: false,
      modal: true,
      styleClass: 'upload-document-dialog',
    });

    this.ref = dialog;
    return dialog;
  }

  /**
   * Opens the Enable Authenticator App dialog
   * @param data Optional data to pass to the dialog
   * @returns The dialog reference
   */
  openEnableAuthenticatorDialog(data?: any) {
    const dialog = this.open(EnableAuthenticatorDialogComponent, {
      width: '450px',
      height: 'auto',
      closable: false,
      showHeader: false,
      modal: true,
      styleClass: 'enable-authenticator-dialog',
      data,
    });

    this.ref = dialog;
    return dialog;
  }

  /**
   * Opens the Authenticator App Setup dialog
   * @param data Optional data to pass to the dialog
   * @returns The dialog reference
   */
  openAuthenticatorSetupDialog(data?: any) {
    const dialog = this.open(AuthenticatorSetupDialogComponent, {
      width: '500px',
      height: 'auto',
      closable: true,
      showHeader: false,
      modal: true,
      styleClass: 'authenticator-setup-dialog',
      data,
    });

    this.ref = dialog;
    return dialog;
  }

  /**
   * Opens the Payment Link Created dialog
   * @param linkUrl The payment link URL to display
   * @returns The dialog reference
   */
  openPaymentLinkCreatedDialog(linkUrl: string) {
    const dialog = this.open(PaymentLinkCreatedDialogComponent, {
      width: '420px',
      height: 'auto',
      closable: false,
      showHeader: false,
      modal: true,
      baseZIndex: 1000, // Lower than toast (99999)
      styleClass: 'payment-link-created-dialog',
      data: { linkUrl },
    });

    this.ref = dialog;
    return dialog;
  }

  /**
   * Opens the Session Ended dialog
   * @param data Optional data to customize the dialog (title, description, illustration)
   * @returns The dialog reference
   */
  openSessionEndedDialog(data?: { title?: string; description?: string; illustration?: string }) {
    const dialog = this.open(SessionEndedDialogComponent, {
      width: '464px',
      height: 'auto',
      closable: false,
      showHeader: false,
      modal: true,
      styleClass: 'session-ended-dialog',
      data,
    });

    this.ref = dialog;
    return dialog;
  }

  openWaitingCardSetupDialog(data?: any) {
    const dialog = this.open(WaitingCardSetupDialogComponent, {
      width: '400px',
      height: 'auto',
      closable: false,
      showHeader: false,
      modal: true,
      styleClass: 'waiting-card-setup-dialog',
      data,
    });

    this.ref = dialog;
    return dialog;
  }
}
